import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { NAV, canAccess } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const ROLE_LABELS = {
  super_admin: "Super Admin", direktur: "Direktur", finance: "Finance",
  hr: "HR / Admin", site_admin: "Site Admin", viewer: "Viewer",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const role = user?.role;
  const [logoSrc, setLogoSrc] = useState("/logo.png");
  useEffect(() => {
    api.get("/settings").then((r) => { if (r.data?.logo_path) setLogoSrc(fileUrl(r.data.logo_path)); }).catch(() => {});
  }, []);
  const location = useLocation();
  const [pendingKasbon, setPendingKasbon] = useState(0);
  const canApprove = ["super_admin", "finance", "direktur"].includes(role);
  useEffect(() => {
    if (!canApprove) return;
    const load = () => api.get("/kasbon-requests/pending-count").then((r) => setPendingKasbon(r.data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [canApprove, location.pathname]);
  const [pendingNota, setPendingNota] = useState(0);
  const isSA = role === "super_admin";
  useEffect(() => {
    if (!isSA) return;
    const load = () => api.get("/nota-pengajuan-pending-count").then((r) => setPendingNota(r.data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("nota-changed", load);
    return () => { clearInterval(t); window.removeEventListener("nota-changed", load); };
  }, [isSA, location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const SidebarContent = (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800 shrink-0">
        <img src={logoSrc} alt="Coating Solution" className="h-10 w-10 rounded-lg bg-white object-contain p-1" />
        <div className="leading-tight">
          <div className="font-heading font-extrabold tracking-tight text-white">COATING SOLUTION</div>
          <div className="text-[10px] uppercase tracking-widest text-blue-300">HR Management System</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scroll-thin px-3 py-4 space-y-5">
        {NAV.map((group) => {
          const items = group.items.filter((it) => canAccess(it.roles, role, user?.extra_menus, it.to));
          if (!items.length) return null;
          return (
            <div key={group.title}>
              <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {group.title}
              </div>
              <div className="space-y-0.5">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.to === "/" || it.to === "/nota"}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-${it.to.replace(/\//g, "-") || "home"}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-blue-700 text-white"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{it.label}</span>
                      {it.to === "/kasbon-requests" && canApprove && pendingKasbon > 0 && (
                        <span data-testid="kasbon-pending-badge" className="ml-auto rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white">
                          {pendingKasbon}
                        </span>
                      )}
                      {it.to === "/nota/persetujuan" && isSA && pendingNota > 0 && (
                        <span data-testid="nota-pending-badge" className="ml-auto rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white">
                          {pendingNota}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-40">{SidebarContent}</aside>

      {/* Mobile drawer */}
      <div className={`lg:hidden fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <div className={`absolute inset-y-0 left-0 w-72 max-w-[82%] shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <button onClick={() => setOpen(false)} data-testid="drawer-close"
            className="absolute right-3 top-4 z-10 rounded-md p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white">
            <X className="h-5 w-5" />
          </button>
          {SidebarContent}
        </div>
      </div>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 sm:px-6 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="sidebar-toggle"
              onClick={() => setOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <span className="text-sm font-semibold text-slate-500 hidden sm:block">
              COATING SOLUTION — HR Management System
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger"
                className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-3 hover:bg-slate-50">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-800 text-sm font-bold text-white">
                  {(user?.name || "?").charAt(0)}
                </span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-sm font-semibold text-slate-800">{user?.name}</span>
                  <span className="block text-[11px] text-slate-500">{ROLE_LABELS[role] || role}</span>
                </span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn"
                onClick={async () => { await logout(); navigate("/login"); }}>
                <LogOut className="mr-2 h-4 w-4" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 space-y-6">{children}</main>
      </div>
    </div>
  );
}
